import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ActivityFeedItemViewModel from '@/Components/ActivityFeed/Items/ActivityFeedItemViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import ActivityFeedOptionsWindowViewModel from '@/Components/ActivityFeed/Options/ActivityFeedOptionsWindowViewModel';
import LinkViewModelBuilder from '@/Builder/LinkViewModelBuilder';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import RADApplication from '@/Management/RADApplication';
import { IControlViewModelConstructorParameter } from '#/CoreFrameInterfaces';
import { IActivityFeedTimeUnitGroupDTO } from "coreframetypings/dto";

type TimeUnitGroup = {
    title: string;
    items: ActivityFeedItemViewModel[];
}

interface IActivityFeedViewModelParams extends IControlViewModelConstructorParameter {
}

export default class ActivityFeedViewModel extends ControlViewModel {

    public title: LabelViewModel;
    public optionsImage: ImageViewModel;
    public optionsWindow: ActivityFeedOptionsWindowViewModel;
    public timeUnitGroups: KnockoutObservable<TimeUnitGroup[]>;
    public loadMoreLink: LinkViewModel;
    public showLoadMoreLink: KnockoutObservable<boolean>;
    public moreEntriesAvailable: KnockoutObservable<boolean>;
    public moreDataMultiplier: number;
    public moreDataLoaded: KnockoutObservable<boolean>;
    public noElements: LabelViewModel;
    public items: KnockoutObservableArray<ActivityFeedItemViewModel>;

    constructor(params: IActivityFeedViewModelParams) {
        super(params);

        this.title = this.createTitle();
        this.optionsImage = this.createOptionsImage();
        this.optionsWindow = this.createOptionsWindow();
        this.timeUnitGroups = this.observableOrDefault([]);
        this.loadMoreLink = this.createLoadMoreLink();
        this.moreEntriesAvailable = this.observableOrDefault(false);
        this.moreDataMultiplier = 1;
        this.moreDataLoaded = this.observableOrDefault(false).extend({ notify: 'always' });
        this.showLoadMoreLink = this.defineComputed(() => {
            const groups = this.timeUnitGroups();
            const moreDataAvailable = this.moreEntriesAvailable();
            return groups.length > 0 && moreDataAvailable;
        });
        this.noElements = this.createNoElementsLabel();
    }

    public refresh(reset: boolean): JQuery.Promise<void> {
        if (reset) {
            this.moreDataMultiplier = 1;
        }

        return this.loadData().then(groups => {
            if (this.isDisposed)
                return;

            this.timeUnitGroups(groups);
        });
    }

    private createTitle(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName("ACTIVITY_FEED.TITLE")
            .build();
    }

    private createOptionsImage(): ImageViewModel {
        return new ImageViewModelBuilder()
            .withSource('gearwheel.svg')
            .build();
    }

    private createOptionsWindow(): ActivityFeedOptionsWindowViewModel {
        return new ActivityFeedOptionsWindowViewModel({
            isModal: true,
            isOpen: false,
            allowClose: true,
            width: 300,
            height: 75,
            parentLocator: this.locator,
            locator: 'options-window',
            headline: new LabelViewModelBuilder()
                .withErgName('ACTIVITY_FEED.OPTIONS.TITLE')
                .build(),
            onClose: () => {
                this.refresh(true);
            },
        });
    }

    private createLoadMoreLink(): LinkViewModel {
        return new LinkViewModelBuilder()
            .withLabel()
                .withErgName("ACTIVITY_FEED.LOAD_MORE")
                .done()
            .withOnClick(() => {
                this.moreDataMultiplier += 1;
                this.refresh(false).then(() => {
                    if (this.isDisposed)
                        return;

                    this.moreDataLoaded(true);
                });
            })
            .build();
    }

    private createNoElementsLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName("ACTIVITY_FEED.NO_ELEMENTS")
            .build();
    }

    public openOptions(): void {
        this.optionsWindow.isOpen(true);
    }

    private loadData(): JQuery.Promise<TimeUnitGroup[]> {
        const itemAmount = +this.optionsWindow.entryAmount() * this.moreDataMultiplier;
        const language: string = RADApplication.getInstance().currentLanguage();
        this.increaseBusyCounter();

        return RADEnvironmentProxy.getInstance().getActivityFeed({ itemAmount, language })
            .then((result) => {
                if (this.isDisposed)
                    return [];

                this.moreEntriesAvailable(result.moreEntriesAvailable);
                return this.mapFromDTO(result.timeUnitGroups);
            }).always(() => {
                if (this.isDisposed)
                    return;

                this.decreaseBusyCounter();
            });
    }

    private mapFromDTO(groups: IActivityFeedTimeUnitGroupDTO[]): TimeUnitGroup[] {
        return groups.map(g => {
            return {
                title: g.title,
                items: g.items.map((item, idx) => new ActivityFeedItemViewModel({ activityItemDTO: item, unit: g.unit, itemIndex: idx }))
            };
        });
    }
}

ComponentManager.setViewModel('ActivityFeed', ActivityFeedViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');